/**
 * Deutsche Lokalisierung für @blockly/toolbox-search.
 * Registriert das Plugin und überschreibt die englischen Texte.
 * Erweitert die Suche um Tooltip-basiertes Matching.
 */
import * as Blockly from 'blockly';
import '@blockly/toolbox-search';

/** Modul-globaler Tooltip-Index: blockType → tooltip-Text */
const blockTooltipIndex = new Map<string, string>();

/** Menge aller veralteten Block-Typen – werden aus der Suche ausgeschlossen. */
const deprecatedBlockTypes = new Set<string>();

/** Wird von blockly-parser.service.ts nach jeder Block-Registrierung aufgerufen. */
export function registerBlockTooltip(type: string, tooltip: string) {
  blockTooltipIndex.set(type, tooltip);
}

/** Wird von blockly-parser.service.ts für veraltete Blöcke aufgerufen. */
export function registerDeprecatedBlock(type: string) {
  deprecatedBlockTypes.add(type);
}

// Suppress "editable field not serializable" warning from Blockly.
// Root cause: Blockly's Field class defines `SERIALIZABLE = false` as a class field (own instance
// property), so patching the prototype has no effect. Suppressing via console.warn is the only
// reliable cross-package solution (also covers @blockly/field-dependent-dropdown etc.).
const _origWarn = console.warn.bind(console);
console.warn = (...args: unknown[]) => {
  if (typeof args[0] === 'string' && args[0].includes('editable field that was not serializable')) return;
  _origWarn(...args);
};

const SEARCH_CATEGORY_KIND = 'search';

const LABEL_TRANSLATIONS: Record<string, string> = {
  'Type to search for blocks': 'Suchbegriff eingeben…',
  'No matching blocks found': 'Keine passenden Blöcke gefunden',
};

// Hole die registrierte Klasse aus dem Registry
const RegisteredClass = Blockly.registry.getClass(
  Blockly.registry.Type.TOOLBOX_ITEM,
  SEARCH_CATEGORY_KIND,
) as typeof Blockly.ToolboxCategory;

if (RegisteredClass) {
  class ToolboxSearchCategoryDE extends RegisteredClass {
    private ourSearchField?: HTMLInputElement;

    protected override createDom_(): HTMLDivElement {
      const dom = super.createDom_();
      this.ourSearchField = dom.querySelector('input') as HTMLInputElement;
      if (this.ourSearchField) {
        this.ourSearchField.placeholder = 'Block suchen…';
      }
      return dom;
    }

    override onNodeBlur(): void {
      // Suchbegriff und dazugehörige Treffer werden beim Wechsel in den Workspace beibehalten
    }

    override getContents(): Blockly.utils.toolbox.FlyoutItemInfoArray | string {
      const base = super.getContents();
      if (!Array.isArray(base)) return base;

      // Übersetze System-Labels
      for (const item of base) {
        if ('text' in item && typeof (item as any).text === 'string' && LABEL_TRANSLATIONS[(item as any).text]) {
          (item as any).text = LABEL_TRANSLATIONS[(item as any).text];
        }
      }

      // Veraltete Blöcke aus den Basis-Treffern entfernen
      const filteredBase = base.filter(i =>
        (i as any).kind !== 'block' || !deprecatedBlockTypes.has((i as any).type ?? '')
      );

      const query = this.ourSearchField?.value?.trim() ?? '';
      if (query.length < 2) return filteredBase;

      const lowerQuery = query.toLowerCase();
      const existingTypes = new Set(
        filteredBase
          .filter((i): i is Blockly.utils.toolbox.BlockInfo => (i as any).kind === 'block')
          .map(i => i.type)
          .filter((t): t is string => !!t)
      );

      const tooltipMatches: Blockly.utils.toolbox.BlockInfo[] = [];
      for (const [type, tooltip] of blockTooltipIndex) {
        if (!existingTypes.has(type) && tooltip.toLowerCase().includes(lowerQuery)) {
          tooltipMatches.push({ kind: 'block', type } as Blockly.utils.toolbox.BlockInfo);
        }
      }

      if (tooltipMatches.length === 0) return filteredBase;

      // Wenn filteredBase nur ein "Keine Treffer"-Label enthält, durch Tooltip-Treffer ersetzen
      const baseHasOnlyLabel = filteredBase.every(i => (i as any).kind === 'label');
      const filtered = baseHasOnlyLabel ? [] : filteredBase;
      return [...filtered, ...tooltipMatches];
    }
  }

  // Überschreibe die Standard-Registrierung mit der deutschen Variante
  Blockly.registry.unregister(Blockly.registry.Type.TOOLBOX_ITEM, SEARCH_CATEGORY_KIND);
  Blockly.registry.register(Blockly.registry.Type.TOOLBOX_ITEM, SEARCH_CATEGORY_KIND, ToolboxSearchCategoryDE);
}
